import { useEffect, useState, useContext } from 'react';
import { convertDateFormat } from '../../helpers/date-time';
import {
  WeatherDataProps,
  WeatherDataCity,
  WeatherDataList,
} from '../../utils/types';
import useGetTokens from '../../hooks/useGetTokens';
import { SettingsContext } from '../../../App';

export const useWeather = (location?: string) => {
  const { getDisplayUnit } = useContext(SettingsContext);
  const isFahrenheit = getDisplayUnit(location);
  const weatherToken = useGetTokens()?.WeatherApiAccessKey;

  let cityCounty = ' ';
  let city = '';

  // Map location names to API query parameters
  switch (location?.toLocaleLowerCase()) {
    case 'rome':
      cityCounty = 'Rome, Italy';
      city = 'rome,it';
      break;
    case 'austin':
      cityCounty = 'Austin, Texas';
      city = 'austin,us';
      break;
    case 'maui':
      cityCounty = 'Maui, Hawaii';
      city = 'kahului,us';
      break;
    case 'lisbon':
      cityCounty = 'Lisbon, Portugal';
      city = 'lisbon,pt';
      break;
    case 'big island':
      cityCounty = 'Big Island, Hawaii';
      city = 'puako,us';
      break;
    case 'lisbon - corinthia':
      cityCounty = 'Lisbon, Portugal';
      city = 'lisbon,pt';
      break;
    case 'lisbon - epic sana':
      cityCounty = 'Lisbon, Portugal';
      city = 'lisbon,pt';
      break;
    case 'riviera maya':
      cityCounty = 'Riviera Maya, Mexico';
      city = 'cancun,mx';
      break;
    case 'carlsbad':
      cityCounty = 'Carlsbad, California';
      city = 'carlsbad,ca,us';
      break;
    case 'monte carlo':
      cityCounty = 'Monte Carlo, Monaco';
      city = 'monte carlo,mc';
      break;
    case 'prague':
      cityCounty = 'Prague, Czech Republic';
      city = 'prague,cz';
      break;
    case 'san diego':
      cityCounty = 'San Diego, California';
      city = 'san diego,ca,us';
      break;
    case 'singapore':
      cityCounty = 'Singapore';
      city = 'singapore,sg';
      break;
    // Add a default case or handle unknown locations if necessary
    default:
      // Optionally set a default location or handle the error
      cityCounty = 'Unknown Location';
      city = ''; // Or a default city like 'london,uk'
      break;
  }

  const [isLoading, setIsLoading] = useState(true);
  const [weatherData, setWeatherData] = useState<WeatherDataProps | null>(null);
  const [weatherDataList, setWeatherDataList] = useState<WeatherDataList[]>([]);
  const [weatherDataCity, setWeatherDataCity] =
    useState<WeatherDataCity | null>(null);

  // Determine the units parameter for the API call based on the context setting
  const unitsToUse = isFahrenheit ? 'imperial' : 'metric';

  useEffect(() => {
    const getWeatherData = async () => {
      if (!city || !weatherToken) {
        setIsLoading(false);
        console.error('City or Weather Token is missing.');
        return; // Exit if city or token is not available
      }
      setIsLoading(true); // Set loading true at the start of fetch
      try {
        const response = await fetch(
          `https://api.openweathermap.org/data/2.5/forecast?q=${city}&units=${unitsToUse}&cnt=8&appid=${weatherToken}`
        );
        if (!response.ok) {
          // Handle HTTP errors like 404 or 401
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (data && data.city && data.list) {
          setWeatherDataCity(data.city);
          setWeatherData(data);
          setWeatherDataList(data.list);
        } else {
          // Handle cases where the API returns unexpected data structure
          console.error('Invalid weather data structure:', data);
          setWeatherDataCity(null);
          setWeatherData(null);
          setWeatherDataList([]);
        }
      } catch (error) {
        console.error('Failed to fetch weather data:', error);
        // Reset state on error
        setWeatherDataCity(null);
        setWeatherData(null);
        setWeatherDataList([]);
      } finally {
        setIsLoading(false); // Ensure loading is set to false after fetch attempt
      }
    };

    getWeatherData();
    // Dependency array includes variables that trigger a re-fetch when changed
  }, [city, weatherToken, unitsToUse]);

  // Helper function to convert UNIX timestamp + timezone offset to local time details
  const localTimeConverter = (unixTime: number, timezoneInSeconds: number) => {
    // Calculate local time by adjusting UTC with the timezone offset
    const localTime = new Date((unixTime + timezoneInSeconds) * 1000);
    const formattedDate = convertDateFormat(localTime); // Use your existing helper

    return {
      localTime, // Raw Date object
      hour: formattedDate.time, // Formatted time string (e.g., "10:00 AM")
      weekDay: formattedDate.dayOfWeek, // Formatted day of week (e.g., "Monday")
      month: formattedDate.month, // Formatted month (e.g., "April")
      date: formattedDate.day, // Day of the month (e.g., 23)
    };
  };

  // Function to get the formatted date string for display
  const getDateToDisplay = () => {
    const dt = weatherData?.list?.[0]?.dt;
    const timezone = weatherDataCity?.timezone; // Use weatherDataCity which is now typed correctly
    if (dt !== undefined && timezone !== undefined) {
      const dateDisplay = localTimeConverter(dt, timezone);
      return `${dateDisplay.weekDay}, ${dateDisplay.month} ${dateDisplay.date}`;
    }
    return ''; // Return empty string if data is not available
  };

  // Function to get the formatted time for a specific forecast item
  const timeForecast = (dtItem: number) => {
    const timezone = weatherDataCity?.timezone;
    if (dtItem !== undefined && timezone !== undefined) {
      return localTimeConverter(dtItem, timezone).hour;
    }
    return ''; // Return empty string if data is not available
  };

  return {
    cityCounty, // Display name (e.g., "Rome, Italy")
    weatherData, // Full API response (can be null)
    weatherDataCity, // City details from API (can be null)
    weatherDataList, // Array of forecast items
    getDateToDisplay, // Function to get formatted date string
    timeForecast, // Function to get formatted time for a forecast item
    isLoading, // Boolean indicating if data is loading
    isFahrenheit, // Boolean indicating the current unit setting
  };
};
